import React from 'react';
import {
    List,
    Datagrid,
    TextField,
    ReferenceField,
    BooleanField,
    DateField,
    FunctionField,
    EditButton,
    DeleteButton,
    useTranslate,
    TopToolbar,
    CreateButton,
    ExportButton,
    RefreshButton,
    Filter,
    TextInput,
    ReferenceInput,
    SelectInput,
    BooleanInput,
    useListContext,
    RecordContextProvider,
    useGetIdentity,
} from 'react-admin';
import { Box, Chip, Typography, Fab, useMediaQuery, useTheme } from '@mui/material';
import { CreditCard, Person, Business, LocationOn, CheckCircle, Cancel, Add as AddIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const RfidCardsFilter = (props: any) => {
    const translate = useTranslate();
    const { data: identity } = useGetIdentity();
    // Vendor-scope: restrict the vendor filter dropdown to the actor's assigned
    // vendors so a scoped admin can't enumerate other merchants by name.
    const actorVendorIds: string[] = ((identity as any)?.vendorIds as string[]) || [];
    const isVendorScoped = actorVendorIds.length > 0;

    return (
        <Filter {...props}>
            <TextInput
                label={translate('resources.rfid-cards.list.filters.search')}
                source="q"
                alwaysOn
            />
            <ReferenceInput
                source="vendorId"
                reference="vendors"
                label={translate('resources.rfid-cards.fields.vendor')}
                filter={isVendorScoped ? { id: actorVendorIds } : undefined}
            >
                <SelectInput optionText="name" />
            </ReferenceInput>
            <BooleanInput
                source="isActive"
                label={translate('resources.rfid-cards.fields.isActive')}
            />
            <TextInput
                source="cardType"
                label={translate('resources.rfid-cards.fields.cardType')}
            />
        </Filter>
    );
};

const DesktopListActions = () => (
    <TopToolbar>
        <CreateButton />
        <ExportButton />
        <RefreshButton />
    </TopToolbar>
);

const MobileListActions = () => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0 }}>
        <ExportButton />
        <RefreshButton />
    </Box>
);

const MobileList = () => {
    const { data, isLoading } = useListContext();
    const navigate = useNavigate();
    if (isLoading || !data) return null;
    return (
        <Box>
            {data.map((record: any) => (
                <RecordContextProvider key={record.id} value={record}>
                    <Box onClick={() => navigate(`/rfid-cards/${record.id}/show`)}
                        sx={{ py: 1.5, px: 2, borderBottom: '1px solid #F0F0F0', cursor: 'pointer' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                                <CreditCard sx={{ fontSize: 16, color: '#666', flexShrink: 0 }} />
                                <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {record.cardNumber}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                                <Chip label={record.cardType || 'STANDARD'} size="small" color={record.cardType === 'ADMIN' ? 'secondary' : 'default'} sx={{ height: 22, fontSize: '0.65rem' }} />
                                <Chip
                                    icon={record.isActive ? <CheckCircle /> : <Cancel />}
                                    label={record.isActive ? 'Active' : 'Inactive'}
                                    color={record.isActive ? 'success' : 'default'}
                                    size="small"
                                    sx={{ height: 22, fontSize: '0.65rem', '& .MuiChip-icon': { fontSize: '0.85rem' } }}
                                />
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Person sx={{ fontSize: 14, color: '#999' }} />
                                <Typography variant="caption" color="text.secondary">
                                    {record.userName || 'N/A'}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Business sx={{ fontSize: 14, color: '#999' }} />
                                <Typography variant="caption" color="text.secondary">
                                    {record.vendorName || 'N/A'}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <LocationOn sx={{ fontSize: 14, color: '#999' }} />
                                <Typography variant="caption" color="text.secondary">
                                    {record.sites?.length || 0} sites
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </RecordContextProvider>
            ))}
        </Box>
    );
};

export const RfidCardsList = () => {
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();

    return (
        <Box sx={{ position: 'relative' }}>
            <List
                actions={isMobile ? <MobileListActions /> : <DesktopListActions />}
                filters={<RfidCardsFilter />}
                empty={isMobile ? false : undefined}
                sort={{ field: 'createdAt', order: 'DESC' }}
                perPage={25}
                sx={{
                    '& .RaList-content': {
                        backgroundColor: isMobile ? 'transparent' : '#FAFAFA',
                    },
                    ...(isMobile && {
                        '& .RaList-main': { margin: 0 },
                        '& .RaList-content': { backgroundColor: 'transparent !important', boxShadow: 'none !important', mt: 2 },
                        '& .RaList-actions': { backgroundColor: 'transparent !important', p: '0 !important', minHeight: 'auto !important' },
                        '& .RaListToolbar-root': { backgroundColor: 'transparent !important', flexDirection: 'row !important', flexWrap: 'wrap !important', alignItems: 'center !important', gap: '8px !important' },
                        '& .RaListToolbar-root > span': { display: 'none' },
                        '& .RaFilterButton-root, & .add-filter': { display: 'none !important' },
                        '& .RaListToolbar-root > form, & .RaFilterForm-root': { flex: '1 1 100% !important', width: '100% !important' },
                        '& .filter-field, & .RaFilterForm-filterFormInput': { width: '100% !important' },
                        '& .filter-field .MuiFormControl-root': { width: '100% !important' },
                        '& .filter-field .MuiInputBase-root': { width: '100% !important' },
                        '& .MuiToolbar-root': { backgroundColor: 'transparent !important', minHeight: 'auto !important' },
                    }),
                }}
            >
                {isMobile ? (
                    <MobileList />
                ) : (
                    <Datagrid
                        rowClick="show"
                        bulkActionButtons={false}
                        sx={{
                            '& .RaDatagrid-headerCell': {
                                backgroundColor: '#F5F5F5',
                                fontWeight: 'bold',
                            },
                            '& .RaDatagrid-row': {
                                '&:hover': {
                                    backgroundColor: '#F0F7FF',
                                },
                            },
                        }}
                    >
                        <TextField
                            source="cardNumber"
                            label={translate('resources.rfid-cards.fields.cardNumber')}
                            sx={{ fontFamily: 'monospace', fontWeight: 600 }}
                        />
                        <FunctionField
                            label={translate('resources.rfid-cards.fields.user')}
                            render={record => (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Person sx={{ fontSize: 16, color: '#666' }} />
                                    <Box>
                                        <Typography variant="body2">{record.userName || 'N/A'}</Typography>
                                        {record.userEmail && (
                                            <Typography variant="caption" color="text.secondary">
                                                {record.userEmail}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            )}
                        />
                        <FunctionField
                            label={translate('resources.rfid-cards.fields.vendor')}
                            render={record => (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Business sx={{ fontSize: 16, color: '#666' }} />
                                    <Typography variant="body2">{record.vendorName || 'N/A'}</Typography>
                                </Box>
                            )}
                        />
                        <FunctionField
                            label={translate('resources.rfid-cards.fields.cardType')}
                            render={record => (
                                <Chip
                                    label={record.cardType || 'STANDARD'}
                                    size="small"
                                    color={record.cardType === 'ADMIN' ? 'secondary' : 'default'}
                                />
                            )}
                        />
                        <FunctionField
                            label={translate('resources.rfid-cards.fields.assignedSites')}
                            render={record => {
                                const siteCount = record.sites?.length || 0;
                                return (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <LocationOn sx={{ fontSize: 16, color: '#666' }} />
                                        <Typography variant="body2">
                                            {siteCount} {translate('resources.rfid-cards.siteCount', { count: siteCount })}
                                        </Typography>
                                    </Box>
                                );
                            }}
                        />
                        <BooleanField
                            source="isActive"
                            label={translate('resources.rfid-cards.fields.isActive')}
                        />
                        <DateField
                            source="createdAt"
                            label={translate('resources.rfid-cards.fields.createdAt')}
                            showTime
                        />
                        <FunctionField
                            label={translate('resources.rfid-cards.fields.lastUsed')}
                            render={record => {
                                if (!record.lastUsedAt) {
                                    return <Typography variant="body2" color="text.secondary">Never</Typography>;
                                }
                                return <DateField source="lastUsedAt" showTime record={record} />;
                            }}
                        />
                        <FunctionField
                            label=""
                            render={record => (
                                <Box sx={{ display: 'flex', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                                    <EditButton record={record} />
                                    {!record.lastUsedAt && (record.currentUses ?? 0) === 0 && (
                                        <DeleteButton record={record} mutationMode="pessimistic" />
                                    )}
                                </Box>
                            )}
                        />
                    </Datagrid>
                )}
            </List>
            {isMobile && (
                <Fab color="primary" onClick={() => navigate('/rfid-cards/create')}
                    sx={{ position: 'fixed', bottom: 24, right: 24, backgroundColor: '#009A75', '&:hover': { backgroundColor: '#007A5E' }, zIndex: 1100 }}>
                    <AddIcon />
                </Fab>
            )}
        </Box>
    );
};
